/// <amd-module name="Test/TestPlansHubRefresh/TestSuiteImportPanel/Redux/TestSuiteImportPanelActions" />
import { ActionsUnion, createAction } from "redux/Utils/ActionHelper";
import { TeamProjectReference } from "Tfs/Platform/Contracts/Core";
import { TestPlan, TestSuite } from "TestPlanCommon/Contracts/TestPlan";

export const TestSuiteImportPanelActions = {
    /**
     * copyTestSuite: copy the selected test suite into the destination test suite
     */
    copyTestSuite: (
        sourceTestSuiteId: number,
        sourceProject: string,
        destinationTestSuiteId: number,
        destinationPlanId: number,
        destinationAreaPath: string,
        destinationIterationPath: string
    ) =>
        createAction(TestSuiteImportPanelActionsTypes.copyTestSuite, {
            sourceTestSuiteId,
            sourceProject,
            destinationTestSuiteId,
            destinationPlanId,
            destinationAreaPath,
            destinationIterationPath
        }),

    /**
     * mandatoryFieldUpdated: this action is dispatched to update the flag for evaluating if all mandatory fields are updated.
     */
    mandatoryFieldUpdated: (isMandatoryFieldUpdated: boolean) =>
        createAction(TestSuiteImportPanelActionsTypes.mandatoryFieldUpdated, isMandatoryFieldUpdated),

    /**
     * copyTestSuiteCompleted: close the panel
     */
    copyTestSuiteCompleted: (opId: number) => createAction(TestSuiteImportPanelActionsTypes.copyTestSuiteCompleted, { opId }),

    /**
     * copyTestSuiteInProgress: invoke the reducer to show copying is in progress
     */
    copyTestSuiteInProgress: () => createAction(TestSuiteImportPanelActionsTypes.copyTestSuiteInProgress),

    /**
     * getProjectsFromServer: fetch the projects via an async call
     */
    getProjectsFromServer: () => createAction(TestSuiteImportPanelActionsTypes.getProjectsFromServer),

    /**
     * projectsFetchSucceeded: invoke the reducer to get the list of projects
     */
    projectsFetchSucceeded: (projectList: TeamProjectReference[]) =>
        createAction(TestSuiteImportPanelActionsTypes.projectsFetchSucceeded, projectList),

    /**
     * getTestPlans: fetch the list of test plans under the chosen project
     */
    getTestPlans: (projectName: string, continuationToken?: string) =>
        createAction(TestSuiteImportPanelActionsTypes.getTestPlans, { projectName, continuationToken }),

    /**
     * testPlansFetchSucceeded: invoke the reducer to get the list of test plans under the chosen project
     */
    testPlansFetchSucceeded: (project: string, planList: TestPlan[]) =>
        createAction(TestSuiteImportPanelActionsTypes.testPlansFetchSucceeded, { project, planList }),

    /**
     * getTestSuitesForPlan: fetch the list of test suites under the chosen test plan
     */
    getTestSuitesForPlan: (project: string, planId: number) =>
        createAction(TestSuiteImportPanelActionsTypes.getTestSuitesForPlan, { project, planId }),

    /**
     * getTestSuitesForPlanSucceeded: invoke the reducer to get the list of test suites under the chosen test plan
     */
    getTestSuitesForPlanSucceeded: (suiteList: TestSuite[]) =>
        createAction(TestSuiteImportPanelActionsTypes.getTestSuitesForPlanSucceeded, suiteList),

    /**
     * testSuiteImportPanelError: invoke the reducer to render any error while import suite process
     */
    testSuiteImportPanelError: (testPlanSelectionErrorMessage: string) =>
        createAction(TestSuiteImportPanelActionsTypes.testSuiteImportPanelError, { testPlanSelectionErrorMessage }),

    /**
     * testSuiteImportPanelSuiteSelectionErrorMessage: invoke the reducer to render any error while selecting suite
     */
    testSuiteImportPanelSuiteSelectionError: (testSuiteSelectionErrorMessage: string) =>
        createAction(TestSuiteImportPanelActionsTypes.testSuiteImportPanelSuiteSelectionError, { testSuiteSelectionErrorMessage }),

    /**
     * disableTestSuiteEditableDropdownSelection: invoke the reducer to disable the suite selection dropdown
     */
    disableTestSuiteEditableDropdownSelection: (disableTestSuiteDropdown: boolean) =>
        createAction(TestSuiteImportPanelActionsTypes.disableTestSuiteEditableDropdownSelection, disableTestSuiteDropdown)

};

export const enum TestSuiteImportPanelActionsTypes {
    copyTestSuite = "TestSuiteImportPanel/copyTestSuite",
    mandatoryFieldUpdated = "TestSuiteImportPanel/mandatoryFieldUpdated",
    copyTestSuiteInProgress = "TestSuiteImportPanel/copyTestSuiteInProgress",
    copyTestSuiteCompleted = "TestSuiteImportPanel/copyTestSuiteCompleted",
    getProjectsFromServer = "TestSuiteImportPanel/getProjectsFromServer",
    projectsFetchSucceeded = "TestSuiteImportPanel/projectsFetchSucceeded",
    getTestPlans = "TestSuiteImportPanel/getTestPlans",
    testPlansFetchSucceeded = "TestSuiteImportPanel/testPlansFetchSucceeded",
    getTestSuitesForPlan = "TestSuiteImportPanel/getTestSuitesForPlan",
    getTestSuitesForPlanSucceeded = "TestSuiteImportPanel/getTestSuitesForPlanSucceeded",
    testSuiteImportPanelError = "TestSuiteImportPanel/testSuiteImportPanelError",
    testSuiteImportPanelSuiteSelectionError = "TestSuiteImportPanel/testSuiteImportPanelSuiteSelectionError",
    disableTestSuiteEditableDropdownSelection = "TestSuiteImportPanel/disableTestSuiteEditableDropdownSelection",
}

export type TestSuiteImportPanelActions = ActionsUnion<typeof TestSuiteImportPanelActions>;
